<template>
  <div
    :id="componentId"
    class="w-100 px-0 d-flex flex-row justify-content-between align-items-center mb-space-xs"
    role="group"
    :aria-labelledby="`${componentId}-title`"
  >
    <h3 :id="`${componentId}-title`" class="text-left" :class="titleClass">
      {{ title }}
    </h3>
  </div>
  <div v-for="option in options" :key="option.value" class="form-check mb-space-xxs">
    <input
      :id="`${componentId}-${option.value}`"
      class="form-check-input"
      type="checkbox"
      :checked="selected?.has(option.value)"
      :aria-checked="selected?.has(option.value)"
      :aria-labelledby="`${componentId}-${option.value}-label`"
      @click="handleChecked(option)"
    />
    <label
      :id="`${componentId}-${option.value}-label`"
      class="form-check-label d-inline-flex align-items-center fs-medium"
      :for="`${componentId}-${option.value}`"
    >
      {{ option?.text }}
      <ToolTip v-if="showTooltip && option.description">
        <template #tooltip>
          <div class="fs-medium" v-html="option.description"></div>
        </template>
        <font-awesome-icon class="info-icon text-primary" icon="info-circle"></font-awesome-icon>
      </ToolTip>
    </label>
  </div>
</template>

<script setup lang="ts">
import ToolTip from "~/components/ToolTip.vue";
import type { TitleVariant, VariantOption } from "~/types/types";
export type FilterOption = {
  value: string;
  text: string;
  description?: string;
};

type Props = {
  componentId: string;
  title: string;
  options: FilterOption[];
  showTooltip?: boolean;
  titleSize?: TitleVariant;
  titleVariant?: VariantOption;
};
const props = withDefaults(defineProps<Props>(), {
  showTooltip: false,
  titleSize: "small",
  titleVariant: "dark-3"
});

/*
 ********************************************
 *  Emits
 ********************************************
 */
const emits = defineEmits<{
  (e: "checked" | "unchecked" | "change", value: FilterOption): void;
}>();
/*
 ********************************************
 *  data
 ********************************************
 */
const selected = defineModel<Set<string>>({ required: true });
/*
 ********************************************
 * computed
 ********************************************
 */
const titleClass = computed(() => {
  return `h3-${props.titleSize} fs-${props.titleVariant}`;
});
/*
 ********************************************
 *  methods
 ********************************************
 */
const handleChecked = (option: FilterOption) => {
  if (selected.value?.has(option.value)) {
    selected.value.delete(option.value);
    emits("unchecked", option);
  } else {
    selected.value?.add(option.value);
    emits("checked", option);
  }
  emits("change", option);
};
</script>

<style scoped></style>
